import { Download } from "lucide-react";
import { appendFyToSearchParams } from "../../utils/dashboardFilters";
import { SectionHeader, SnowflakeRequired } from "../SectionHeader";
import RAGBadge from "../RAGBadge";
import ThemeChip from "../ThemeChip";

export function IndicatorDetails({
  sf, indicators, indPage, setIndPage, indTypeFilter, setIndTypeFilter,
  API, filters,
}) {
  return (
    <SectionHeader title="Indicator Details" subtitle="KPI targets vs actuals with RAG status" collapsible defaultOpen={false}>
      {!sf ? (
        <SnowflakeRequired message="KPI indicator details require the program measurement data source." />
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="indicator-type-filter">
            {["Activity", "Output", "Outcome", "Impact"].map(t => (
              <button key={t}
                onClick={() => { setIndTypeFilter(t); setIndPage(1); }}
                data-testid={`type-chip-${t.toLowerCase()}`}
                className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${indTypeFilter === t
                  ? "bg-[#006837] text-white border-[#006837]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#006837] hover:text-[#006837]"}`}>
                {t}
              </button>
            ))}
            <span className="text-xs text-slate-600 ml-1">{indicators.total.toLocaleString("en-IN")} records</span>
            <a
              href={`${API}/grantee/indicators/export?${(() => { const sp = new URLSearchParams(); appendFyToSearchParams(sp, filters.fy); if (filters.theme) sp.append("theme", filters.theme); if (indTypeFilter) sp.append("indicator_type", indTypeFilter); return sp.toString(); })()}`}
              download
              data-testid="export-csv-btn"
              className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 hover:text-[#006837] hover:border-[#006837] transition-colors">
              <Download size={12} />
              Export CSV
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="indicator-details-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase py-2 pr-2">Grant ID</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Grantee</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Indicator</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Theme</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">FY</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Target YTD</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Actual YTD</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Achievement</th>
                  <th className="text-center text-xs font-medium text-slate-500 uppercase py-2 group relative cursor-help">
                    <span className="border-b border-dotted border-slate-400">RAG</span>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-56 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity text-left">
                      <div className="font-semibold mb-1">RAG Status (Calculated)</div>
                      <div className="space-y-0.5">
                        <div><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>Green: ≥80% achievement</div>
                        <div><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1"></span>Amber: 50-79% achievement</div>
                        <div><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1"></span>Red: &lt;50% achievement</div>
                      </div>
                    </div>
                  </th>
                  <th className="text-center text-xs font-medium text-slate-500 uppercase py-2">MIS</th>
                </tr>
              </thead>
              <tbody>
                {indicators.data.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 text-xs text-slate-600 pr-2 whitespace-nowrap font-mono">{r.fluxx_id || "–"}</td>
                    <td className="py-2 text-xs text-slate-700 max-w-[120px] truncate font-medium" title={r.grantee}>{r.grantee || "–"}</td>
                    <td className="py-2 text-xs max-w-xs truncate font-medium text-slate-900" title={r.indicator}>{r.indicator}</td>
                    <td className="py-2"><ThemeChip theme={r.theme} /></td>
                    <td className="py-2 text-xs text-slate-500 whitespace-nowrap">{r.fy}</td>
                    <td className="py-2 text-right text-xs">{r.target?.toLocaleString("en-IN")}</td>
                    <td className="py-2 text-right text-xs font-medium">{r.actual?.toLocaleString("en-IN")}</td>
                    <td className="py-2 text-right text-xs font-bold">{r.achievement_pct?.toFixed(1)}%</td>
                    <td className="py-2 text-center"><RAGBadge status={r.rag} size="sm" /></td>
                    <td className="py-2 text-center text-xs">
                      {r.mis === "MIS"
                        ? <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">MIS</span>
                        : <span className="text-slate-500">–</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {indicators.total > 30 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-500">{(indPage - 1) * 30 + 1}–{Math.min(indPage * 30, indicators.total)} of {indicators.total.toLocaleString("en-IN")}</span>
                <div className="flex gap-2">
                  <button onClick={() => setIndPage(p => Math.max(1, p - 1))} disabled={indPage === 1}
                    className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Prev</button>
                  <button onClick={() => setIndPage(p => p + 1)} disabled={indPage * 30 >= indicators.total}
                    className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </SectionHeader>
  );
}
